import { useFlag } from '@unleash/proxy-client-react';
import { t } from 'i18next';
import { useTranslation } from 'react-i18next';

import BrlImage from 'assets/images/countries/BRL.svg';
import CopImage from 'assets/images/countries/COP.svg';
import EurImage from 'assets/images/countries/EUR.svg';
import MxnImage from 'assets/images/countries/MXN.svg';
import UsdImage from 'assets/images/countries/USD.svg';

import { FormField } from 'components/form/formField/formField';
import { IconEnvelope } from 'components/icons/envelope';
import { Input } from 'components/inputs';
import { Select, SelectItem } from 'components/inputs/select/select';
import OptionallyVisible from 'components/optionallyVisible';

import { AccountType, PwcCustomerType } from 'types/account';
import { CurrencyCode } from 'types/currency';
import { RequestPagination, ServerPagination } from 'types/pagination';

import { RadioGroup } from '../../../inputs/radio/radioGroup';
import { RemoteSelect } from '../../../inputs/select/remoteSelect';
import { KEYS, LABELS } from '../keys';
import { Delimeter } from '../styles';
import { CreatedAccount } from '../types';
import {
  AccountOwnerSection,
  AccountSection,
  Adornment,
  FiatCurrencySection,
  GroupAccountSection,
  Label,
  MerchantIdSection,
  Optional,
} from './form.styles';

interface CreateAccountFormProps {
  formValues: CreatedAccount;
  onFieldValueChange: (key: string, value: any) => void;
  error?: Record<string, string>;
  accountType: AccountType;
  setAccountType: (accountType: AccountType) => void;
  setPwcCustomerType: (type: PwcCustomerType) => void;
  pwcCustomerType: PwcCustomerType;
  getGroupAccounts: (params: {
    search?: string;
    pagination?: RequestPagination;
    ids?: string | string[];
  }) => Promise<{ data: SelectItem[]; pagination?: ServerPagination }>;
  getParentAccounts: (params: {
    search?: string;
    pagination?: RequestPagination;
    ids?: string | string[];
  }) => Promise<{ data: SelectItem[]; pagination?: ServerPagination }>;
}

const blockOptions = [
  {
    key: AccountType.Ramp,
    value: AccountType.Ramp,
    label: t(LABELS.CREATE_MODAL.HEADER.RAMP_TITLE),
    disabled: true,
  },
  {
    key: AccountType.Pwc,
    value: AccountType.Pwc,
    selectedOn: [AccountType.Pwc, AccountType.Pwc_Group],
    label: t(LABELS.CREATE_MODAL.HEADER.PWC_TITLE),
  },
];

const pwcOptions = [
  {
    key: AccountType.Pwc,
    value: AccountType.Pwc,
    label: t(LABELS.CREATE_MODAL.PWC_OPTIONS.MERCHANT),
  },
  {
    key: AccountType.Pwc_Group,
    value: AccountType.Pwc_Group,
    label: t(LABELS.CREATE_MODAL.PWC_OPTIONS.GROUP_ACCOUNT),
  },
];

const pwcCustomerTypeOptions = [
  {
    key: PwcCustomerType.Business,
    value: PwcCustomerType.Business,
    label: t(LABELS.CREATE_MODAL.PWC_CUSTOMER_OPTIONS.BUSINESS),
  },
  {
    key: PwcCustomerType.Individual,
    value: PwcCustomerType.Individual,
    label: t(LABELS.CREATE_MODAL.PWC_CUSTOMER_OPTIONS.INDIVIDUAL),
  },
];

const currencyIcons: Record<CurrencyCode, string> = {
  [CurrencyCode.BRL]: BrlImage,
  [CurrencyCode.COP]: CopImage,
  [CurrencyCode.EUR]: EurImage,
  [CurrencyCode.MXN]: MxnImage,
  [CurrencyCode.USD]: UsdImage,
};

const FIAT_CURRENCY_FLAGS: Partial<Record<CurrencyCode, string>> = {
  [CurrencyCode.BRL]: 'brl-fiat',
  [CurrencyCode.MXN]: 'mxn-fiat',
  [CurrencyCode.COP]: 'cop-fiat',
};

export const CreateAccountModalForm = ({
  formValues,
  error,
  onFieldValueChange,
  accountType,
  setAccountType,
  getGroupAccounts,
  getParentAccounts,
  setPwcCustomerType,
  pwcCustomerType,
}: CreateAccountFormProps) => {
  const { t } = useTranslation();
  const isBrlEnabled = useFlag(FIAT_CURRENCY_FLAGS[CurrencyCode.BRL] ?? '');
  const isMxnEnabled = useFlag(FIAT_CURRENCY_FLAGS[CurrencyCode.MXN] ?? '');
  const isCopEnabled = useFlag(FIAT_CURRENCY_FLAGS[CurrencyCode.COP] ?? '');

  const currencySelectOptions: SelectItem[] = Object.values(CurrencyCode)
    .filter((currencyCode) => {
      if (currencyCode === CurrencyCode.BRL) return isBrlEnabled;
      if (currencyCode === CurrencyCode.MXN) return isMxnEnabled;
      if (currencyCode === CurrencyCode.COP) return isCopEnabled;
      return true;
    })
    .map((currencyCode) => ({
      icon: currencyIcons[currencyCode],
      key: currencyCode.toString(),
      label: t(LABELS.CREATE_MODAL.CURRENCY_LABELS[currencyCode]),
    }));

  const handleFormChange = (fieldName: string) => (value: any) => {
    onFieldValueChange(fieldName, value);
  };

  const handleAccountTypeChange = (type: string) => {
    setAccountType(type as AccountType);
  };

  const handleGroupAccountChange = (item: SelectItem | SelectItem[]) => {
    handleFormChange(KEYS.FORM_FIELDS.GROUP_ACCOUNT_ID)((item as SelectItem).key);
  };

  const handleParentAccountChange = (item: SelectItem | SelectItem[]) => {
    const selected = item as SelectItem;
    onFieldValueChange(KEYS.FORM_FIELDS.PARENT_ACCOUNT_UUID, selected.key);
    onFieldValueChange('parentAccountName', selected.label);
  };

  const getSelectedCurrencySelectItem = (currencyCode: string) => {
    return currencySelectOptions.find((item) => item.key === currencyCode);
  };

  const onCurrencySelect = (item: SelectItem | null) => {
    handleFormChange(KEYS.FORM_FIELDS.MAIN_FIAT_CURRENCY)((item as SelectItem).key as CurrencyCode);
  };

  return (
    <>
      <RadioGroup horizontal options={blockOptions} onChange={handleAccountTypeChange} value={accountType} />

      <Delimeter />

      <OptionallyVisible visible={accountType === AccountType.Pwc || accountType === AccountType.Pwc_Group}>
        <RadioGroup horizontal options={pwcOptions} onChange={handleAccountTypeChange} value={accountType} />
      </OptionallyVisible>

      <AccountSection>
        <Label>{t(LABELS.CREATE_MODAL.SECTIONS.ACCOUNT_SECTION.TITLE)}</Label>
        <FormField error={error?.accountName || error?.name}>
          <Input
            placeholder={t(LABELS.CREATE_MODAL.FORM_FIELDS.PLACEHOLDERS.ACCOUNT_NAME)}
            value={formValues.accountName}
            onChange={handleFormChange(KEYS.FORM_FIELDS.ACCOUNT_NAME)}
          />
        </FormField>
      </AccountSection>

      <OptionallyVisible visible={accountType === AccountType.Pwc && pwcCustomerType === PwcCustomerType.Business}>
        <Label>{t(LABELS.CREATE_MODAL.SECTIONS.FIAT_CURRENCY_SECTION.TITLE)}</Label>
        <FormField error={error?.mainFiatCurrency}>
          <FiatCurrencySection>
            <Select
              fullWidth
              closeDropdownOnSelect
              getItemLabel={({ label }) => label}
              popperProps={{
                disablePortal: true,
              }}
              items={currencySelectOptions}
              value={getSelectedCurrencySelectItem(formValues.mainFiatCurrency)}
              onChange={onCurrencySelect}
              error={Boolean(error?.mainFiatCurrency)}
            />
          </FiatCurrencySection>
        </FormField>
      </OptionallyVisible>

      <OptionallyVisible visible={accountType === AccountType.Pwc}>
        <GroupAccountSection>
          <Label>
            {t(LABELS.CREATE_MODAL.SECTIONS.GROUP_ACCOUNT_SECTION.TITLE)}
            <OptionallyVisible visible={pwcCustomerType === PwcCustomerType.Individual}>
              {' '}
              <Optional>{t(LABELS.CREATE_MODAL.FORM_FIELDS.OPTIONAL)}</Optional>
            </OptionallyVisible>
          </Label>
          <FormField fullWidth error={error?.groupAccountId}>
            <RemoteSelect<SelectItem, boolean>
              fullWidth
              value={formValues.groupAccountId}
              onChange={handleGroupAccountChange}
              multiple={false}
              placeholder={t(LABELS.CREATE_MODAL.FORM_FIELDS.PLACEHOLDERS.GROUP_ACCOUNT_ID)}
              getItems={getGroupAccounts}
              getItemLabel={(item) => item.label}
              closeDropdownOnSelect
            />
          </FormField>
        </GroupAccountSection>
      </OptionallyVisible>

      <OptionallyVisible visible={accountType === AccountType.Pwc && pwcCustomerType === PwcCustomerType.Business}>
        <MerchantIdSection>
          <Label>
            {t(LABELS.CREATE_MODAL.SECTIONS.MERCHANT_ID_SECTION.TITLE)} <Optional>{t(LABELS.CREATE_MODAL.FORM_FIELDS.OPTIONAL)}</Optional>
          </Label>
          <FormField error={error?.shift4MerchantId}>
            <Input
              placeholder={t(LABELS.CREATE_MODAL.FORM_FIELDS.PLACEHOLDERS.MERCHANT_ID)}
              value={formValues.shift4MerchantId}
              onChange={handleFormChange(KEYS.FORM_FIELDS.MERCHANT_ID)}
            />
          </FormField>
        </MerchantIdSection>
      </OptionallyVisible>

      <OptionallyVisible visible={accountType === AccountType.Pwc_Group}>
        <GroupAccountSection>
          <Label>{t(LABELS.CREATE_MODAL.SECTIONS.PARENT_ACCOUNT_SECTION.TITLE)}</Label>
          <FormField fullWidth error={error?.parentAccountUuid}>
            <RemoteSelect<SelectItem, boolean>
              fullWidth
              value={formValues.parentAccountUuid}
              onChange={handleParentAccountChange}
              multiple={false}
              placeholder={t(LABELS.CREATE_MODAL.FORM_FIELDS.PLACEHOLDERS.PARENT_ACCOUNT_UUID)}
              getItems={getParentAccounts}
              getItemLabel={(item) => item.label}
              closeDropdownOnSelect
            />
          </FormField>
        </GroupAccountSection>
      </OptionallyVisible>

      <AccountOwnerSection>
        <Label>{t(LABELS.CREATE_MODAL.SECTIONS.ACCOUNT_OWNER_SECTION.TITLE)}</Label>
        <FormField error={error?.username || error?.ownerEmail}>
          <Input
            placeholder={t(LABELS.CREATE_MODAL.FORM_FIELDS.PLACEHOLDERS.EMAIL)}
            value={formValues.username}
            onChange={handleFormChange(KEYS.FORM_FIELDS.EMAIL)}
            startAdornment={
              <Adornment>
                <IconEnvelope />
              </Adornment>
            }
          />
        </FormField>

        <FormField error={error?.firstName || error?.ownerFullName}>
          <Input
            placeholder={t(LABELS.CREATE_MODAL.FORM_FIELDS.PLACEHOLDERS.FIRST_NAME)}
            value={formValues.firstName}
            onChange={handleFormChange(KEYS.FORM_FIELDS.FIRST_NAME)}
          />
        </FormField>

        <FormField error={error?.lastName || error?.ownerFullName}>
          <Input
            placeholder={t(LABELS.CREATE_MODAL.FORM_FIELDS.PLACEHOLDERS.LAST_NAME)}
            value={formValues.lastName}
            onChange={handleFormChange(KEYS.FORM_FIELDS.LAST_NAME)}
          />
        </FormField>
      </AccountOwnerSection>

      <OptionallyVisible visible={accountType === AccountType.Pwc}>
        <RadioGroup
          horizontal
          options={pwcCustomerTypeOptions}
          onChange={(val) => setPwcCustomerType(val as PwcCustomerType)}
          value={pwcCustomerType}
        />
      </OptionallyVisible>
    </>
  );
};
